/* eslint-disable @nx/enforce-module-boundaries */
import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from 'apps/job-ats/src/environments/environment';
import { Observable } from 'rxjs';
import {
  AgeGroup,
  AverageRecruitmentDelayResult,
  GenderGroup,
  GeographicalGroup,
  MonthlyRecruitmentCarriedOut,
  OfferAcceptanceRate,
  OfferStatusGroup,
  OfferStatusStructure,
  StatisticsCriteria,
} from '../utils/dashboard.data';
import { Job } from '@job-v2/ats-utils';

@Injectable({
  providedIn: 'root',
})
export class DashboardService {
  private baseApiUrl = `${environment.apiUrl}/recruitment/statistics`;
  private readonly http = inject(HttpClient);

  getTotalApplications(criteria: StatisticsCriteria): Observable<number> {
    let params = new HttpParams();
    if (criteria.companyId)
      params = params.set('companyId', criteria.companyId);
    if (criteria.offer)
      params = params.set('offer', criteria.offer);
    if (criteria.year) params = params.set('year', criteria.year);
    if (criteria.contractType)
      params = params.set('contractType', criteria.contractType);
    if (criteria.department)
      params = params.set('department', criteria.department);
    params = this.appendSelectedJobOwners(params, criteria.selectedJobOwner);
    return this.http.get<number>(`${this.baseApiUrl}/applications-received`, {
      params,
    });
  }

  getTotalAcceptedApps(criteria: StatisticsCriteria): Observable<number> {
    let params = new HttpParams();
    if (criteria.companyId)
      params = params.set('companyId', criteria.companyId);
    if (criteria.monthYear)
      params = params.set('monthYear', criteria.monthYear);
    if (criteria.department)
      params = params.set('department', criteria.department);
    params = this.appendSelectedJobOwners(params, criteria.selectedJobOwner);
    return this.http.get<number>(`${this.baseApiUrl}/applications-accepted`, {
      params,
    });
  }

  getTotalPositionsToFill(criteria: StatisticsCriteria): Observable<number> {
    let params = new HttpParams();
    if (criteria.companyId)
      params = params.set('companyId', criteria.companyId);
    if (criteria.monthYear)
      params = params.set('monthYear', criteria.monthYear);
    if (criteria.offer) params = params.set('offer', criteria.offer);
    params = this.appendSelectedJobOwners(params, criteria.selectedJobOwner);
    return this.http.get<number>(`${this.baseApiUrl}/positions-to-fill`, {
      params,
    });
  }

  getAgeDistribution(companyId: string): Observable<AgeGroup[]> {
    return this.http.get<AgeGroup[]>(
      `${this.baseApiUrl}/${companyId}/age-distribution`,
    );
  }

  getGenderDistribution(companyId: string): Observable<GenderGroup[]> {
    return this.http.get<GenderGroup[]>(
      `${this.baseApiUrl}/${companyId}/gender-distribution`,
    );
  }

  getGeographicalDistribution(
    companyId: string,
  ): Observable<GeographicalGroup[]> {
    return this.http.get<GeographicalGroup[]>(
      `${this.baseApiUrl}/${companyId}/geographical-distribution`,
    );
  }

  getOffersStatusStats(
    criteria: StatisticsCriteria,
  ): Observable<OfferStatusGroup[]> {
    let params = new HttpParams();
    if (criteria.companyId)
      params = params.set('companyId', criteria.companyId);
    if (criteria.monthYear)
      params = params.set('monthYear', criteria.monthYear);
    params = this.appendSelectedJobOwners(params, criteria.selectedJobOwner);
    return this.http.get<OfferStatusGroup[]>(
      `${this.baseApiUrl}/offers-status`,
      { params },
    );
  }

  getOfferAcceptanceRate(
    criteria: StatisticsCriteria,
  ): Observable<OfferAcceptanceRate> {
    let params = new HttpParams();
    if (criteria.companyId)
      params = params.set('companyId', criteria.companyId);
    if (criteria.monthYear)
      params = params.set('monthYear', criteria.monthYear);
    if (criteria.offer) params = params.set('offer', criteria.offer);
    params = this.appendSelectedJobOwners(params, criteria.selectedJobOwner);
    return this.http.get<OfferAcceptanceRate>(
      `${this.baseApiUrl}/offers-acceptance-rate`,
      { params },
    );
  }

  getOfferApplicationsCount(
    criteria: StatisticsCriteria,
  ): Observable<OfferAcceptanceRate> {
    let params = new HttpParams();
    if (criteria.offer)
      params = params.set('offer', criteria.offer);
    return this.http.get<OfferAcceptanceRate>(
      `${this.baseApiUrl}/offers-acceptance-rate`,
      { params },
    );
  }

  getMonthlyRecruitmentsCarriedOut(
    criteria: StatisticsCriteria,
  ): Observable<MonthlyRecruitmentCarriedOut[]> {
    let params = new HttpParams();
    if (criteria.companyId)
      params = params.set('companyId', criteria.companyId);
    if (criteria.year) params = params.set('year', criteria.year);
    params = this.appendSelectedJobOwners(params, criteria.selectedJobOwner);
    return this.http.get<MonthlyRecruitmentCarriedOut[]>(
      `${this.baseApiUrl}/recruitments-carried-out`,
      { params },
    );
  }

  getAverageRecruitmentDelay(
    criteria: StatisticsCriteria,
  ): Observable<AverageRecruitmentDelayResult[]> {
    let params = new HttpParams();
    if (criteria.companyId)
      params = params.set('companyId', criteria.companyId);
    if (criteria.year) params = params.set('year', criteria.year);
    params = this.appendSelectedJobOwners(params, criteria.selectedJobOwner);
    return this.http.get<AverageRecruitmentDelayResult[]>(
      `${this.baseApiUrl}/average-recruitment-delay`,
      { params },
    );
  }

  getAveragePublicationToClosingDelay(
    criteria: StatisticsCriteria,
  ): Observable<AverageRecruitmentDelayResult[]> {
    let params = new HttpParams();
    if (criteria.companyId)
      params = params.set('companyId', criteria.companyId);
    if (criteria.year) params = params.set('year', criteria.year);
    params = this.appendSelectedJobOwners(params, criteria.selectedJobOwner);
    return this.http.get<AverageRecruitmentDelayResult[]>(
      `${this.baseApiUrl}/average-publication-closing`,
      { params },
    );
  }

  getOverallStatusPerStructure(
    criteria: StatisticsCriteria,
  ): Observable<OfferStatusStructure[]> {
    let params = new HttpParams();
    if (criteria.companyId)
      params = params.set('companyId', criteria.companyId);
    if (criteria.year) params = params.set('year', criteria.year);
    params = this.appendSelectedJobOwners(params, criteria.selectedJobOwner);
    return this.http.get<OfferStatusStructure[]>(
      `${this.baseApiUrl}/overall-status-structure`,
      { params },
    );
  }

  getAcceptanceRatePerStructure(
    criteria: StatisticsCriteria,
  ): Observable<OfferStatusStructure[]> {
    let params = new HttpParams();
    if (criteria.companyId)
      params = params.set('companyId', criteria.companyId);
    if (criteria.year) params = params.set('year', criteria.year);
    params = this.appendSelectedJobOwners(params, criteria.selectedJobOwner);
    return this.http.get<OfferStatusStructure[]>(
      `${this.baseApiUrl}/acceptance-rate-structures`,
      { params },
    );
  }

  getLatestOffers(
    companyId: string,
    selectedJobOwner?: string[],
  ): Observable<Job[]> {
    let params = new HttpParams();
    params = this.appendSelectedJobOwners(params, selectedJobOwner);
    return this.http.get<Job[]>(
      `${this.baseApiUrl}/${companyId}/latest-offers`,
      { params },
    );
  }

  private appendSelectedJobOwners(
    params: HttpParams,
    selectedJobOwner?: string[],
  ): HttpParams {
    if (!selectedJobOwner?.length) return params;
    return selectedJobOwner.reduce(
      (currentParams, ownerId) =>
        currentParams.append('selectedJobOwner', ownerId),
      params,
    );
  }
}
